/**
 * Header and Footer (HNF) Monitor
 * Provides monitoring and analytics for header/footer loading issues
 */

import logger from './logger';
import { pushEvent } from './analyticsApi';

// Feature flag for Adobe Analytics tracking
// Set to 'true' to enable Adobe Analytics tracking for HNF monitoring
// Set to 'false' to disable tracking (current default)
const doTrackInAdobeAnalytics = false;

class HNFMonitor {
  constructor() {
    this.events = [];
    this.startTime = Date.now();
    this.headerStatus = 'unknown';
    this.footerStatus = 'unknown';
    this.ssrConfig = null;

    // Initialize monitoring
    this.initializeMonitoring();
  }

  initializeMonitoring() {
    // Capture SSR configuration if available
    if (typeof window !== 'undefined' && window.LEARNONE_HNF_CONFIG) {
      this.ssrConfig = window.LEARNONE_HNF_CONFIG;
      this.logEvent('ssr_config_detected', {
        headerSSRSucceeded: this.ssrConfig.headerSSRSucceeded,
        footerSSRSucceeded: this.ssrConfig.footerSSRSucceeded,
      });
    }

    // Monitor DOM changes
    this.observeContainers();

    // Set up periodic checks
    this.setupPeriodicChecks();
  }

  logEvent(eventType, data = {}) {
    const event = {
      type: eventType,
      timestamp: Date.now(),
      timeFromStart: Date.now() - this.startTime,
      data,
    };

    this.events.push(event);

    // Log important events
    if (['header_loaded', 'footer_loaded', 'fallback_triggered', 'error', 'ssr_config_detected', 'header_status_change', 'footer_status_change', 'header_ssr_failure', 'footer_ssr_failure', 'health_check'].includes(eventType)) {
      logger.info(`HNF Monitor: ${eventType}`, event);
    }
  }

  observeContainers() {
    if (typeof window === 'undefined' || !window.MutationObserver) return;

    const headerContainer = document.getElementById('lo-uh-container');
    const footerContainer = document.getElementById('lo-uf-container');

    // Check if container is a real DOM node before observing
    if (headerContainer && (headerContainer.nodeType || typeof headerContainer.nodeType === 'undefined')) {
      try {
        const headerObserver = new MutationObserver(() => {
          this.checkHeaderStatus();
        });
        headerObserver.observe(headerContainer, { childList: true, subtree: true });
      } catch (error) {
        // Ignore errors in test environment where containers might be mocks
        console.debug('Could not observe header container:', error.message);
      }
    }

    if (footerContainer && (footerContainer.nodeType || typeof footerContainer.nodeType === 'undefined')) {
      try {
        const footerObserver = new MutationObserver(() => {
          this.checkFooterStatus();
        });
        footerObserver.observe(footerContainer, { childList: true, subtree: true });
      } catch (error) {
        // Ignore errors in test environment where containers might be mocks
        console.debug('Could not observe footer container:', error.message);
      }
    }
  }

  setupPeriodicChecks() {
    // Check status at regular intervals
    const checkIntervals = [1000, 3000, 5000, 10000, 30000];

    checkIntervals.forEach((interval) => {
      setTimeout(() => {
        this.performHealthCheck();
      }, interval);
    });
  }

  checkHeaderStatus() {
    const container = document.getElementById('lo-uh-container');
    if (!container) {
      this.headerStatus = 'container_missing';
      return;
    }

    const content = container.innerHTML.trim();
    const hasContent = content.length > 100;
    const hasElements = container.children.length > 0;

    let newStatus = 'empty';
    if (hasContent && hasElements) {
      newStatus = 'loaded';
    } else if (hasContent) {
      newStatus = 'partial';
    }

    if (newStatus !== this.headerStatus) {
      this.headerStatus = newStatus;
      this.logEvent('header_status_change', {
        status: newStatus,
        contentLength: content.length,
        elementCount: container.children.length,
        ssrExpected: this.ssrConfig?.headerSSRSucceeded,
      });
    }
  }

  checkFooterStatus() {
    const container = document.getElementById('lo-uf-container');
    if (!container) {
      this.footerStatus = 'container_missing';
      return;
    }

    const content = container.innerHTML.trim();
    const hasContent = content.length > 100;
    const hasElements = container.children.length > 0;

    let newStatus = 'empty';
    if (hasContent && hasElements) {
      newStatus = 'loaded';
    } else if (hasContent) {
      newStatus = 'partial';
    }

    if (newStatus !== this.footerStatus) {
      this.footerStatus = newStatus;
      this.logEvent('footer_status_change', {
        status: newStatus,
        contentLength: content.length,
        elementCount: container.children.length,
        ssrExpected: this.ssrConfig?.footerSSRSucceeded,
      });
    }
  }

  performHealthCheck() {
    this.checkHeaderStatus();
    this.checkFooterStatus();

    const timeFromStart = Date.now() - this.startTime;

    // Check for concerning states
    if (timeFromStart > 5000) {
      if (this.ssrConfig?.headerSSRSucceeded && this.headerStatus !== 'loaded') {
        this.logEvent('header_ssr_failure', {
          expectedSSR: true,
          actualStatus: this.headerStatus,
          timeFromStart,
        });
      }

      if (this.ssrConfig?.footerSSRSucceeded && this.footerStatus !== 'loaded') {
        this.logEvent('footer_ssr_failure', {
          expectedSSR: true,
          actualStatus: this.footerStatus,
          timeFromStart,
        });
      }
    }

    // Log health check summary
    this.logEvent('health_check', {
      headerStatus: this.headerStatus,
      footerStatus: this.footerStatus,
      timeFromStart,
      eventCount: this.events.length,
    });
  }

  getReport() {
    return {
      headerStatus: this.headerStatus,
      footerStatus: this.footerStatus,
      ssrConfig: this.ssrConfig,
      events: this.events,
      totalTime: Date.now() - this.startTime,
      summary: {
        headerWorking: this.headerStatus === 'loaded',
        footerWorking: this.footerStatus === 'loaded',
        ssrHeaderExpected: this.ssrConfig?.headerSSRSucceeded || false,
        ssrFooterExpected: this.ssrConfig?.footerSSRSucceeded || false,
      },
    };
  }

  reportToAnalytics() {
    const report = this.getReport();

    // Report HNF status to Adobe Analytics
    try {
      this.trackHNFStatus(report);
    } catch (error) {
      logger.error('Failed to track HNF status to analytics', error);
    }

    // Log errors for SSR failures
    if (report.summary.ssrHeaderExpected && !report.summary.headerWorking) {
      logger.error('Header SSR failure detected', report);
      try {
        this.trackHNFError('header', 'ssr_failure', report);
      } catch (error) {
        logger.error('Failed to track header error to analytics', error);
      }
    }

    if (report.summary.ssrFooterExpected && !report.summary.footerWorking) {
      logger.error('Footer SSR failure detected', report);
      try {
        this.trackHNFError('footer', 'ssr_failure', report);
      } catch (error) {
        logger.error('Failed to track footer error to analytics', error);
      }
    }

    return report;
  }

  trackHNFStatus(report) {
    if (!doTrackInAdobeAnalytics) {
      return;
    }

    const event = {
      eventData: {
        eventName: 'hnf_status_check',
        values: {
          headerStatus: report.headerStatus,
          footerStatus: report.footerStatus,
          headerWorking: report.summary.headerWorking,
          footerWorking: report.summary.footerWorking,
          ssrEnabled: report.summary.ssrHeaderExpected || report.summary.ssrFooterExpected,
          totalTime: report.totalTime,
          monitorInstance: this.startTime,
        },
      },
    };

    pushEvent({ event });
  }

  trackHNFError(component, errorType, report) {
    if (!doTrackInAdobeAnalytics) {
      return;
    }

    const event = {
      eventData: {
        eventName: 'hnf_error',
        values: {
          errors: [{
            errorLocation: 'hnf_monitor',
            errorType: `${component}_${errorType}`,
            errorCode: report.summary[`${component}Working`] ? 'working' : 'failed',
            errorReason: `${component} SSR expected but not loaded`,
            errorMessage: `Status: ${report[`${component}Status`]}, Expected SSR: ${report.summary[`ssr${component.charAt(0).toUpperCase() + component.slice(1)}Expected`]}`,
            monitorStartTime: this.startTime,
          }],
        },
      },
    };

    pushEvent({ event });
  }
}

// Create singleton instance
let monitorInstance = null;

export const initializeHNFMonitor = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!monitorInstance) {
    monitorInstance = new HNFMonitor();

    // Expose for debugging
    window.HNFMonitor = monitorInstance;

    // Report after initial load
    setTimeout(() => {
      monitorInstance.reportToAnalytics();
    }, 30000);
  }
  return monitorInstance;
};

export const getHNFMonitor = () => monitorInstance;

// Function to reset the monitor instance (for testing)
export const resetHNFMonitor = () => {
  monitorInstance = null;
};
